import React from 'react';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import ReputationIcon from '../subparts/ReputationIcon';
import {
    REPUTATION_SECTION_THEMES,
    REPUTATION_STAT_THEMES,
    getReputationAvatarTheme,
} from '../subparts/reputationTheme';
import {
    buildReputationUrl,
    formatReputationDate,
    getCommentStatusMeta,
    getFeedbackMeta,
    getPatientInitials,
} from '../subparts/reputationQuery';

function OverviewTab({ shortCode, reputationLinks, patientComments, patientFeedbacks }) {
    const reputationUrl = buildReputationUrl(shortCode);

    const handleCopyUrl = async () => {
        if (!reputationUrl) {
            return;
        }

        try {
            await navigator.clipboard.writeText(reputationUrl);
            showSuccess({ title: 'Copied', text: 'Reputation URL copied to clipboard.' });
        } catch (error) {
            console.error('Failed to copy reputation URL', error);
            showError({ title: 'Copy Failed', text: 'Unable to copy URL.' });
        }
    };

    const stats = [
        {
            label: 'Active Links',
            value: reputationLinks?.length ?? 0,
            icon: 'bi-link-45deg',
            theme: REPUTATION_STAT_THEMES.links,
        },
        {
            label: 'Total Comments',
            value: patientComments?.totalElements ?? 0,
            icon: 'bi-chat-dots',
            theme: REPUTATION_STAT_THEMES.comments,
        },
        {
            label: 'Patient Feedback',
            value: patientFeedbacks?.totalElements ?? 0,
            icon: 'bi-emoji-smile',
            theme: REPUTATION_STAT_THEMES.feedback,
        },
        {
            label: 'Short Code',
            value: shortCode?.shortCode || '—',
            icon: 'bi-braces',
            theme: REPUTATION_STAT_THEMES.shortCode,
        },
    ];

    const latestComments = patientComments?.content?.slice(0, 3) || [];
    const latestFeedbacks = patientFeedbacks?.content?.slice(0, 3) || [];

    return (
        <div>
            <div className="reputation-section">
                <div className="reputation-section__heading">
                    <ReputationIcon icon="bi-speedometer2" theme={REPUTATION_SECTION_THEMES.overview} size="md" />
                    <div>
                        <h2 className="reputation-section__title">Overview</h2>
                        <p className="reputation-section__subtitle">
                            Snapshot of your reputation links, comments, and patient feedback
                        </p>
                    </div>
                </div>
            </div>

            <div className="reputation-stats">
                {stats.map((stat) => (
                    <div key={stat.label} className="reputation-stat">
                        <ReputationIcon icon={stat.icon} theme={stat.theme} size="lg" />
                        <div>
                            <span className="reputation-stat__label">{stat.label}</span>
                            <span className="reputation-stat__value">{stat.value}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="reputation-section">
                <div className="reputation-section__heading">
                    <ReputationIcon icon="bi-code-slash" theme={REPUTATION_SECTION_THEMES.shortCode} size="md" />
                    <div>
                        <h3 className="reputation-section__title">Short Code</h3>
                        <p className="reputation-section__subtitle">
                            Your clinic&apos;s unique identifier for reputation URLs
                        </p>
                    </div>
                </div>
            </div>

            {shortCode?.shortCode ? (
                <div className="reputation-shortcode mb-4">
                    <ReputationIcon icon="bi-braces" theme={REPUTATION_STAT_THEMES.shortCode} size="xl" className="mb-3" />
                    <div className="reputation-shortcode__value">{shortCode.shortCode}</div>
                    <p className="text-muted small mb-3">
                        Share this link with patients to collect reviews and feedback.
                    </p>
                    <div className="reputation-shortcode__url mb-3">
                        <ReputationIcon icon="bi-globe2" theme="cyan" size="xs" />
                        {reputationUrl}
                    </div>
                    <button type="button" className="btn btn-sm btn-accent rounded-pill" onClick={handleCopyUrl}>
                        <i className="bi bi-copy me-1"></i>
                        Copy URL
                    </button>
                </div>
            ) : (
                <div className="ecx-page-empty mb-4">
                    <ReputationIcon
                        icon="bi-exclamation-triangle"
                        theme="warning"
                        size="lg"
                        className="reputation-empty-icon"
                    />
                    <h3 className="h6 fw-normal text-dark mb-1">No short code configured</h3>
                    <p className="mb-0 small">Contact support to set up your reputation short code.</p>
                </div>
            )}

            <div className="reputation-section">
                <div className="reputation-section__heading">
                    <ReputationIcon icon="bi-activity" theme={REPUTATION_SECTION_THEMES.activity} size="md" />
                    <div>
                        <h3 className="reputation-section__title">Recent Activity</h3>
                        <p className="reputation-section__subtitle">Latest comments and feedback from patients</p>
                    </div>
                </div>
            </div>

            <div className="reputation-activity-grid">
                <div className="reputation-activity-card">
                    <div className="reputation-activity-card__header">
                        <ReputationIcon icon="bi-chat-dots" theme="purple" size="xs" />
                        Latest Comments
                    </div>
                    <div className="reputation-activity-card__body">
                        {latestComments.length > 0 ? (
                            latestComments.map((comment, index) => {
                                const statusMeta = getCommentStatusMeta(comment.status);

                                return (
                                    <div key={comment.id} className="reputation-activity-item">
                                        <ReputationIcon
                                            round
                                            theme={getReputationAvatarTheme(comment.patientId, index)}
                                            size="md"
                                            className="reputation-avatar"
                                        >
                                            {getPatientInitials(comment.firstName, comment.lastName)}
                                        </ReputationIcon>
                                        <div className="reputation-activity-item__meta">
                                            <div className="d-flex align-items-start justify-content-between gap-2">
                                                <h4 className="reputation-activity-item__title">
                                                    {comment.firstName} {comment.lastName}
                                                </h4>
                                                <span className={statusMeta.className}>
                                                    <i className={`bi ${statusMeta.icon}`}></i>
                                                    {statusMeta.label}
                                                </span>
                                            </div>
                                            <p className="reputation-activity-item__text">{comment.notes || '—'}</p>
                                            <span className="reputation-activity-item__date">
                                                <i className="bi bi-clock me-1"></i>
                                                {formatReputationDate(comment.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="reputation-activity-empty">
                                <ReputationIcon icon="bi-chat-dots" theme="purple" size="sm" />
                                <span>No comments yet</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="reputation-activity-card">
                    <div className="reputation-activity-card__header">
                        <ReputationIcon icon="bi-emoji-smile" theme="success" size="xs" />
                        Recent Feedback
                    </div>
                    <div className="reputation-activity-card__body">
                        {latestFeedbacks.length > 0 ? (
                            latestFeedbacks.map((feedback, index) => {
                                const feedbackMeta = getFeedbackMeta(feedback.feedback);

                                return (
                                    <div key={feedback.id} className="reputation-activity-item">
                                        <ReputationIcon
                                            round
                                            theme={getReputationAvatarTheme(feedback.id, index)}
                                            size="md"
                                            className="reputation-avatar"
                                        >
                                            {feedback.patientName?.charAt(0) || '?'}
                                        </ReputationIcon>
                                        <div className="reputation-activity-item__meta">
                                            <div className="d-flex align-items-start justify-content-between gap-2">
                                                <h4 className="reputation-activity-item__title">{feedback.patientName}</h4>
                                                <span className={feedbackMeta.className}>
                                                    <i className={`bi ${feedbackMeta.icon}`}></i>
                                                    {feedbackMeta.label}
                                                </span>
                                            </div>
                                            <span className="reputation-activity-item__date">
                                                <i className="bi bi-clock me-1"></i>
                                                {formatReputationDate(feedback.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="reputation-activity-empty">
                                <ReputationIcon icon="bi-emoji-smile" theme="success" size="sm" />
                                <span>No feedback yet</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OverviewTab;
